import { useTranslation } from 'react-i18next'
import { useStyletron } from 'baseui'
import { useDispatch, useSelector } from 'react-redux'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import { retrieveAddressFromEnedis, updateEnedisRawDataMeterOption } from './pdlEigibilityCallback'
import formatUTCDate from '@/shared/tools/formatUTCDate'
import {
  selectActiveAddress,
  selectAddressCurrentContractActive,
  selectCharacteristicsCurrentContractActive,
  selectConfig,
  selectCurrentAddressData,
  selectPersonalContactSubscriptionData,
  selectPreferencesContactSubscriptionData,
  selectProfessionalContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import {
  updateAddressData,
  updateElectricityMeterAddressData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { getAddressPrefillData } from '../Address/addressPrefillUtils'
import { selectAuthData } from '../../../Login/AuthSelectors'
import { modulesConfig } from '../../config'

const useElectricityMeter = ({ type = null }) => {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const { errors, isEditable, lastSentDate } = useFormStatus()
  const { energyType, prestationType } = useSelector(selectCharacteristicsCurrentContractActive)
  const { socialReason: proSocialReason } = useSelector(selectProfessionalContactSubscriptionData)
  const { firstName, lastName, zohoId, phone1 } = useSelector(selectPersonalContactSubscriptionData)
  const { dataCollectionAuth, clientType } = useSelector(selectPreferencesContactSubscriptionData)
  const currentAddressData = useSelector(selectCurrentAddressData)
  const { country } = useSelector(selectAuthData)
  const { contactId } = useSelector(selectConfig)
  const electricityMeter =
    type === 'contract'
      ? useSelector(selectAddressCurrentContractActive).electricityMeter || {}
      : currentAddressData.electricityMeter || {}
  const gasMeter =
    type === 'contract'
      ? useSelector(selectAddressCurrentContractActive).gasMeter || {}
      : currentAddressData.gasMeter || {}

  const {
    pdlNo,
    isValidPdl,
    enedisAuth,
    consentDate,
    oldOccupant,
    pdlEligibilityLoading,
    pdlEligibilityPending,
    pdlEligibilityData,
  } = electricityMeter

  const { pceNo } = gasMeter

  const activeAddress = useSelector(selectActiveAddress)

  const electricityMeterUpdate = payload => {
    dispatch(
      updateElectricityMeterAddressData({
        data: payload,
        id: activeAddress,
      }),
    )
  }
  const canRequestMeterData =
    pdlNo?.length === 14 &&
    zohoId?.length > 0 &&
    lastName?.length > 0 &&
    firstName?.length > 0 &&
    phone1?.length > 0 &&
    dataCollectionAuth

  // @ts-ignore
  const meterConfig = modulesConfig[country]?.meter ?? {}
  const shouldHideMissingFieldsNotification =
    canRequestMeterData || (pdlEligibilityData && Object.keys(pdlEligibilityData).length > 0) || country !== 'fr'

  const treatMeterDataInformation = meterInformation => {
    const updatedMeterInformation = updateEnedisRawDataMeterOption(meterInformation)
    if (updatedMeterInformation.elec?.technicalData?.address) {
      const address = retrieveAddressFromEnedis(updatedMeterInformation.elec?.technicalData?.address)
      if (address !== null) {
        const addressUpdate = getAddressPrefillData(address, currentAddressData)

        if (Object.keys(addressUpdate).length > 0) {
          dispatch(updateAddressData({ data: addressUpdate, id: activeAddress }))
        }
      }
    }

    electricityMeterUpdate({
      pdlEligibilityData: updatedMeterInformation,
      pdlEligibilityLoading: false,
    })
  }

  const requestPdlEligibility = () => {
    if (!pdlNo) {
      return
    }

    dispatch({
      type: subscriptionFormActionsV2.METER_MODULE_REQUEST_PDL_DATA_V2,
      body: {
        meterIdElectricity: pdlNo,
        meterIdGas: pceNo,
        lastname: clientType === 'pro' ? proSocialReason : lastName,
        firstname: firstName,
        phone: phone1,
        zohoId,
        consent: true,
        energyType: energyType || 'elec',
        meterType: 'pdl',
        prestationType,
        contactId,
      },
    })
  }

  const handleAuthorizationClick = value => {
    electricityMeterUpdate({
      enedisAuth: true,
      consentDate: formatUTCDate(new Date().toISOString().slice(0, 19)),
    })

    requestPdlEligibility()
  }

  return {
    t,
    oldOccupant,
    contractType: clientType,
    errors,
    lastSentDate,
    pdlEligibilityLoading,
    pdlEligibilityPending,
    pdlEligibilityData,
    canRequestMeterData,
    enedisAuth,
    isValidPdl,
    pdlNo,
    lastName,
    proSocialReason,
    consentDate,
    isEditable,
    enedisAuthHidden: !meterConfig.enedisAuth,
    shouldHideMissingFieldsNotification,
    treatMeterDataInformation,
    elecMeterUpdate: electricityMeterUpdate,
    handleAuthorizationClick,
    css,
  }
}

export default useElectricityMeter
